import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BuiltBy, Logo } from "../components/Brand";
import { Icon, type IconName } from "../components/Icon";
import { LanguagePicker } from "../components/Pickers";
import { Button, Card, Input, RoomCode, useDialog } from "../components/ui";
import { apiUrl } from "../lib/api";
import { useT } from "../lib/i18n";
import { applyTheme } from "../lib/theme";
import { forgetHostedRoom, listHostedRooms, type HostedRoom } from "../lib/storage";

// The front door. Two jobs: start building a board, or join with a code — plus
// enough explanation that someone who never creates a room still understands
// what this is.
export default function Landing() {
  const t = useT();
  const navigate = useNavigate();
  const dialog = useDialog();
  const [code, setCode] = useState("");
  const [checking, setChecking] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [rooms, setRooms] = useState<HostedRoom[]>([]);

  useEffect(() => {
    // Outside a room there is no host to follow, so show the default theme.
    applyTheme(null);
    setRooms(listHostedRooms());
  }, []);

  const join = async (e: React.FormEvent) => {
    e.preventDefault();
    const clean = code.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 4);
    if (clean.length < 4 || checking) return;

    setChecking(true);
    setNotFound(false);
    try {
      const res = await fetch(apiUrl(`/api/rooms/${clean}`));
      if (res.ok) navigate(`/r/${clean}/play`);
      else setNotFound(true);
    } catch {
      setNotFound(true);
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="min-h-full flex flex-col">
      <header className="flex items-center gap-3 px-5 sm:px-8 py-4">
        <Logo size={30} to={null} />
        <div className="flex-1" />
        <LanguagePicker compact />
      </header>

      <main className="flex-1 flex flex-col items-center px-5 sm:px-8">
        <div className="w-full max-w-4xl flex flex-col items-center gap-12 py-10 sm:py-16">
          {/* hero — the h1 carries the search terms, the wordmark carries the brand */}
          <div className="text-center max-w-[38rem]">
            <p className="type-eyebrow text-accent mb-3">OSJ</p>
            <h1 className="type-display text-5xl sm:text-6xl text-accentLight text-balance">
              {t("landingHeadline")}
            </h1>
            <p className="text-ink/70 mt-4 text-lg sm:text-xl leading-relaxed text-balance">
              {t("landingTagline")}
            </p>
            <p className="text-ink/40 mt-3 leading-relaxed text-balance">{t("landingSub")}</p>
          </div>

          {/* the two doors */}
          <div className="w-full grid gap-4 sm:grid-cols-2 items-start">
            <Link
              to="/new"
              className="group min-w-0 rounded-surface bg-accent text-onAccent p-6 flex flex-col gap-1.5 transition-transform duration-150 ease-pop hover:-translate-y-0.5 hover:bg-accentLight shadow-pop"
            >
              <Icon name="grid" size="1.75rem" className="mb-1.5" />
              <span className="type-display text-2xl">{t("createRoom")}</span>
              <span className="text-sm opacity-70 leading-relaxed">{t("createRoomHint")}</span>
              <span className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold">
                {t("startBuilding")}
                <Icon
                  name="arrowRight"
                  className="transition-transform duration-150 group-hover:translate-x-0.5"
                />
              </span>
            </Link>

            <form
              onSubmit={join}
              className="min-w-0 rounded-surface bg-ink/[0.04] border border-line p-6 flex flex-col gap-1.5"
            >
              <Icon name="buzzer" size="1.75rem" className="mb-1.5 text-accentLight" />
              <span className="type-display text-2xl">{t("joinRoom")}</span>
              <span className="text-sm text-ink/50 leading-relaxed">{t("joinRoomHint")}</span>

              <div className="flex gap-2 mt-3 min-w-0">
                <Input
                  value={code}
                  onChange={(e) => {
                    setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 4));
                    setNotFound(false);
                  }}
                  placeholder="ABCD"
                  aria-label={t("enterCode")}
                  aria-invalid={notFound}
                  autoCapitalize="characters"
                  autoCorrect="off"
                  spellCheck={false}
                  className="type-code text-xl sm:text-2xl text-center uppercase h-12 py-0 min-w-0 placeholder:text-ink/30"
                />
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  disabled={code.length < 4 || checking}
                  className="shrink-0 h-12 px-4"
                >
                  {checking ? "…" : t("join")}
                </Button>
              </div>
              {/* Space is reserved so showing the error does not shift the
                  card, but only one tight line of it. */}
              <p
                role={notFound ? "alert" : undefined}
                aria-hidden={!notFound}
                className={`text-xs leading-4 mt-2 min-h-4 min-w-0 transition-opacity ${
                  notFound ? "text-danger opacity-100" : "opacity-0"
                }`}
              >
                {t("roomNotFound")}
              </p>
            </form>
          </div>

          {/* how it works — so a visitor who never creates a room still gets it */}
          <section className="w-full">
            <div className="grid gap-6 sm:grid-cols-3">
              {(
                [
                  ["pencil", "step1Title", "step1Body"],
                  ["qr", "step2Title", "step2Body"],
                  ["buzzer", "step3Title", "step3Body"],
                ] as const
              ).map(([icon, title, body], i) => (
                <div key={title} className="flex flex-col gap-2">
                  <div className="flex items-center gap-2.5">
                    <span className="type-code text-xs text-ink/30">0{i + 1}</span>
                    <Icon name={icon as IconName} className="text-accentLight" />
                  </div>
                  <h3 className="font-semibold">{t(title)}</h3>
                  <p className="text-sm text-ink/50 leading-relaxed">{t(body)}</p>
                </div>
              ))}
            </div>
          </section>

          {/* rooms this device hosts */}
          {rooms.length > 0 && (
            <section className="w-full">
              <div className="flex items-baseline gap-3 mb-3">
                <h2 className="type-eyebrow text-ink/45">{t("yourRooms")}</h2>
                <span className="text-ink/25 text-xs">{t("yourRoomsHint")}</span>
              </div>
              <ul className="flex flex-col gap-2">
                {rooms.map((room) => (
                  <li key={room.code}>
                    <Card className="flex items-center gap-3 p-3 pl-4">
                      <RoomCode code={room.code} className="text-accentLight shrink-0" />
                      <span className="flex-1 min-w-0 truncate text-ink/60 text-sm">
                        {room.title}
                      </span>
                      <Button
                        size="sm"
                        icon="settings"
                        className="shrink-0"
                        onClick={() => navigate(`/r/${room.code}/admin`)}
                      >
                        {/* On a phone the code and the title already fill the
                            row; the gear alone is unambiguous there. */}
                        <span className="hidden sm:inline">{t("reopenAdmin")}</span>
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        icon="x"
                        aria-label={t("forgetRoom")}
                        title={t("forgetRoom")}
                        onClick={async () => {
                          const ok = await dialog.confirm({
                            title: t("forgetRoom"),
                            body: t("forgetRoomConfirm"),
                            confirmLabel: t("forgetRoom"),
                            cancelLabel: t("cancel"),
                            destructive: true,
                          });
                          if (!ok) return;
                          forgetHostedRoom(room.code);
                          setRooms(listHostedRooms());
                        }}
                      />
                    </Card>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      </main>

      <footer className="px-5 sm:px-8 py-6 border-t border-line flex flex-wrap items-center gap-x-5 gap-y-2">
        <BuiltBy />
        <div className="flex-1" />
        <a
          href="https://github.com/younissk"
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1.5 text-sm text-ink/40 hover:text-ink transition-colors"
        >
          <Icon name="github" />
          {t("sourceCode")}
        </a>
      </footer>
    </div>
  );
}
